/**
 * Feb 2026 · Digital Catalogue.
 *
 * Public-facing e-commerce style grid of every article that has at least
 * one image. Layout is intentionally NOT wrapped in the authenticated MES
 * Layout so a customer receiving a "Share" link doesn't need an account to
 * browse. Details view lives at /catalogue/:articleId for direct linking.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { MagnifyingGlassIcon, ShareIcon, XIcon, TrashIcon } from "@phosphor-icons/react";
import { getToken } from "@/lib/secureStorage";
import api from "@/lib/api";

const backendUrl = () => (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const imgSrc = (path) => `${backendUrl()}/api/files/articles/${encodeURIComponent((path || "").replace(/^.*articles\//, ""))}`;

const publicApi = axios.create({ baseURL: `${backendUrl()}/api` });

export default function Catalogue() {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [gradeFilter, setGradeFilter] = useState("");
  const { articleId } = useParams();
  const nav = useNavigate();
  const detail = useMemo(() => rows.find(r => r.id === articleId) || null, [rows, articleId]);
  // Feb 2026 · Admins signed into the MES can remove catalogue images
  // right from the public grid / detail modal. Anonymous visitors never
  // see the delete affordance so the shared "Share" link stays read-only.
  const isAdmin = !!getToken();

  const removeImage = async (article, imageId) => {
    if (!article || !imageId) return;
    if (!window.confirm(`Remove this image from ${article.product_code}? This cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/articles/${article.id}/images/${imageId}`);
      const newImages = data.images || [];
      setRows(prev => prev.map(r => r.id === article.id ? { ...r, images: newImages } : r));
      toast.success("Image removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove image");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [{ data: cat }, { data: t }] = await Promise.all([
          publicApi.get("/catalogue"),
          publicApi.get("/catalogue/article-types"),
        ]);
        setRows(cat.articles || []);
        setTypes(t.article_types || []);
      } catch (e) {
        toast.error("Failed to load catalogue");
      } finally { setLoading(false); }
    })();
  }, []);

  const grades = useMemo(() => {
    const s = new Set(rows.map(r => r.grade).filter(Boolean));
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(r => {
      if (typeFilter && r.article_type !== typeFilter) return false;
      if (gradeFilter && r.grade !== gradeFilter) return false;
      if (!needle) return true;
      const hay = [
        r.product_code, r.description, r.colour, r.article_type,
        r.style_no, r.size, r.grade,
      ].join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q, typeFilter, gradeFilter]);

  const share = async (r) => {
    const url = `${window.location.origin}/catalogue/${r.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: r.product_code, text: `${r.description || r.product_code} · ${r.colour || ""}`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied");
      }
    } catch (e) { /* user cancelled */ }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-0)] text-white" data-testid="catalogue-page">
      {/* Header — intentionally minimal so it feels like a brand catalogue */}
      <header className="sticky top-0 z-30 bg-[var(--bg-1)]/95 backdrop-blur border-b border-[var(--border)]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Narrow Fabric</div>
            <div className="font-display text-xl font-bold">Digital Catalogue</div>
          </div>
          <div className="flex-1 max-w-lg ml-auto relative">
            <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search articles, colours, styles…"
              data-testid="catalogue-search"
              className="w-full h-10 pl-9 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]"
            />
          </div>
          {/* Feb 2026 · Close / Exit — logged-in users always land back on
              the Dashboard (`/`) so their authenticated session flows
              uninterrupted. External visitors (no token) fall back to
              closing the popup or navigating home. */}
          <button
            data-testid="catalogue-close-btn"
            onClick={() => {
              if (getToken()) { nav("/"); return; }
              if (window.opener) { window.close(); return; }
              nav("/");
            }}
            title="Close catalogue"
            className="h-10 px-3 flex items-center gap-2 bg-[var(--bg-0)] border border-[var(--border)] hover:border-red-500/40 hover:text-red-400 rounded-sm text-sm text-zinc-300"
          >
            <XIcon size={14} weight="bold" />
            <span className="hidden sm:inline">Close</span>
          </button>
        </div>
        {/* Filters row */}
        <div className="max-w-7xl mx-auto px-6 pb-4 flex items-center gap-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Type</span>
          <button onClick={() => setTypeFilter("")} className={`h-7 px-3 text-xs rounded-full border ${!typeFilter ? "bg-[var(--accent)] border-[var(--accent)] text-white" : "border-[var(--border)] text-zinc-400 hover:text-white"}`} data-testid="catalogue-type-all">All</button>
          {types.map(t => (
            <button key={t.code} onClick={() => setTypeFilter(t.code)} data-testid={`catalogue-type-${t.code}`} className={`h-7 px-3 text-xs rounded-full border ${typeFilter === t.code ? "bg-[var(--accent)] border-[var(--accent)] text-white" : "border-[var(--border)] text-zinc-400 hover:text-white"}`}>{t.name || t.code}</button>
          ))}
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 mx-2">Grade</span>
          <button onClick={() => setGradeFilter("")} className={`h-7 px-3 text-xs rounded-full border ${!gradeFilter ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-200" : "border-[var(--border)] text-zinc-400 hover:text-white"}`}>All</button>
          {grades.map(g => (
            <button key={g} onClick={() => setGradeFilter(g)} data-testid={`catalogue-grade-${g}`} className={`h-7 px-3 text-xs rounded-full border ${gradeFilter === g ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-200" : "border-[var(--border)] text-zinc-400 hover:text-white"}`}>{g}</button>
          ))}
          <span className="ml-auto text-xs text-zinc-500" data-testid="catalogue-count">{filtered.length} of {rows.length} articles</span>
        </div>
      </header>

      {/* Grid */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center text-zinc-500 py-20">Loading catalogue…</div>
        ) : filtered.length === 0 ? (
          <div className="tactical-card p-16 text-center text-zinc-500" data-testid="catalogue-empty">
            No articles match — try clearing filters or the search.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filtered.map(r => {
              const primary = (r.images || []).find(i => i.is_primary) || (r.images || [])[0];
              return (
                <article key={r.id} data-testid={`catalogue-card-${r.product_code}`} className="tactical-card overflow-hidden group hover:border-[var(--accent)]/60 transition-colors cursor-pointer" onClick={() => nav(`/catalogue/${r.id}`)}>
                  <div className="aspect-square bg-[var(--bg-2)] flex items-center justify-center overflow-hidden relative">
                    {primary ? (
                      <img src={imgSrc(primary.storage_path)} alt={r.product_code} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="text-zinc-600 text-xs">No Image</div>
                    )}
                    {/* Feb 2026 · admin-only quick-remove for the primary
                        image directly from the catalogue card. Always
                        visible (no hover trap) so it's discoverable on
                        touch devices and doesn't need the operator to
                        hunt for it. */}
                    {isAdmin && primary && (
                      <button
                        title="Remove this image"
                        data-testid={`catalogue-remove-${r.product_code}`}
                        onClick={(e) => { e.stopPropagation(); removeImage(r, primary.id); }}
                        className="absolute top-2 right-2 h-8 px-2.5 rounded-sm bg-black/80 hover:bg-red-500/90 border border-red-500/50 text-red-300 hover:text-white text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5 shadow-lg"
                      >
                        <TrashIcon size={11} weight="bold" /> Remove
                      </button>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="text-[10px] uppercase tracking-widest text-[var(--accent)]">{r.article_type}{r.grade ? ` · Grade ${r.grade}` : ""}</div>
                    <div className="font-mono text-sm font-bold text-white truncate mt-0.5" title={r.product_code}>{r.product_code}</div>
                    <div className="text-xs text-zinc-400 mt-1 truncate">{r.description || "—"}</div>
                    <div className="mt-2 flex items-center gap-2 text-[11px] text-zinc-500">
                      {r.colour && <span className="px-2 py-0.5 rounded-full border border-[var(--border)]">{r.colour}</span>}
                      {r.size && <span className="px-2 py-0.5 rounded-full border border-[var(--border)]">{r.size}</span>}
                      {r.width ? <span className="px-2 py-0.5 rounded-full border border-[var(--border)]">{r.width}mm</span> : null}
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <Link to={`/catalogue/${r.id}`} className="text-[11px] text-[var(--accent)] font-semibold" onClick={(e) => e.stopPropagation()}>View →</Link>
                      <button
                        data-testid={`catalogue-share-${r.product_code}`}
                        onClick={(e) => { e.stopPropagation(); share(r); }}
                        className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-white px-2 py-1 rounded-sm border border-[var(--border)]"
                      >
                        <ShareIcon size={11} weight="regular" /> Share
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>

      {/* Deep-link detail dialog */}
      {detail && (
        <div className="fixed inset-0 z-40 bg-black/85 flex items-center justify-center p-6" onClick={() => nav("/catalogue")} data-testid="catalogue-detail">
          <div className="bg-[var(--bg-1)] border border-[var(--border)] rounded-sm max-w-4xl w-full max-h-[92vh] overflow-y-auto grid md:grid-cols-2" onClick={(e) => e.stopPropagation()}>
            <div className="bg-[var(--bg-2)] aspect-square flex items-center justify-center relative group">
              {(detail.images || []).length ? (
                <img src={imgSrc(((detail.images || []).find(i => i.is_primary) || (detail.images || [])[0]).storage_path)} alt={detail.product_code} className="w-full h-full object-contain" />
              ) : <span className="text-zinc-600">No Image</span>}
              {/* Feb 2026 · admin-only remove for the primary image in
                  the detail modal (mirrors the card action). */}
              {isAdmin && (detail.images || []).length > 0 && (
                <button
                  title="Remove this image"
                  data-testid="catalogue-detail-remove-primary"
                  onClick={() => {
                    const primary = (detail.images || []).find(i => i.is_primary) || (detail.images || [])[0];
                    if (primary) removeImage(detail, primary.id);
                  }}
                  className="absolute top-3 left-3 h-9 px-3 rounded-sm bg-black/75 hover:bg-red-500/90 border border-red-500/40 text-red-300 hover:text-white text-[11px] font-mono uppercase tracking-widest flex items-center gap-1.5"
                >
                  <TrashIcon size={13} weight="bold" /> Remove
                </button>
              )}
            </div>
            <div className="p-6 relative">
              <button onClick={() => nav("/catalogue")} className="absolute top-3 right-3 text-zinc-400 hover:text-white" data-testid="catalogue-detail-close"><XIcon size={16} /></button>
              <div className="text-[10px] uppercase tracking-widest text-[var(--accent)]">{detail.article_type}{detail.grade ? ` · Grade ${detail.grade}` : ""}</div>
              <div className="font-display font-bold text-2xl mt-1">{detail.product_code}</div>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{detail.description || "—"}</p>
              <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                {detail.colour && <Spec label="Colour" value={detail.colour} />}
                {detail.size && <Spec label="Size" value={detail.size} />}
                {detail.width ? <Spec label="Width" value={`${detail.width} mm`} /> : null}
                {detail.style_no && <Spec label="Style No" value={detail.style_no} />}
                {detail.hs_code && <Spec label="HS Code" value={detail.hs_code} />}
              </div>
              {/* Thumbnail strip · admin gets an always-visible remove
                  X on every thumbnail. When there's only one image and
                  the operator is admin, we still render the strip so
                  they can drop the last image (unlike the customer view
                  which hides it when there's just one). */}
              {isAdmin && (detail.images || []).length > 0 && (
                <div className="mt-5">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                    All images · {(detail.images || []).length}
                    <span className="ml-2 text-zinc-600 normal-case tracking-normal">— click × to remove</span>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {(detail.images || []).map(im => (
                      <div key={im.id} className="relative shrink-0" data-testid={`catalogue-detail-thumb-${im.id}`}>
                        <img src={imgSrc(im.storage_path)} alt="" className="w-16 h-16 object-cover rounded-sm border border-[var(--border)]" />
                        {im.is_primary && (
                          <span className="absolute bottom-0.5 left-0.5 text-[8px] font-mono uppercase tracking-widest px-1 py-0.5 rounded-sm bg-emerald-500/80 text-white">
                            Primary
                          </span>
                        )}
                        <button
                          title="Remove this image"
                          data-testid={`catalogue-detail-remove-thumb-${im.id}`}
                          onClick={(e) => { e.stopPropagation(); removeImage(detail, im.id); }}
                          className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-md border border-red-700"
                        >
                          <XIcon size={9} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Public visitor thumbnail strip · shown only when there
                  are multiple images and the viewer is NOT an admin
                  (the admin strip above already covers that case). */}
              {!isAdmin && (detail.images || []).length > 1 && (
                <div className="mt-5 flex gap-2 overflow-x-auto">
                  {(detail.images || []).map(im => (
                    <img key={im.id} src={imgSrc(im.storage_path)} alt="" className="w-14 h-14 object-cover rounded-sm border border-[var(--border)] shrink-0" />
                  ))}
                </div>
              )}
              <button
                data-testid="catalogue-detail-share"
                onClick={() => share(detail)}
                className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold"
              >
                <ShareIcon size={13} weight="bold" /> Share this article
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Spec({ label, value }) {
  return (
    <div className="border border-[var(--border)] rounded-sm px-3 py-2">
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-white font-mono">{value}</div>
    </div>
  );
}
